import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import { SignOut, Trash, ArrowClockwise, LockKey } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/content/ContentContext";
import { DEFAULTS } from "@/content/defaults";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const STATUS_STYLES = {
  new: "bg-brand-gold/15 text-brand-gold border-brand-gold/30",
  contacted: "bg-blue-400/10 text-blue-300 border-blue-300/30",
  "in-progress": "bg-emerald-400/10 text-emerald-300 border-emerald-300/30",
  closed: "bg-brand-bone/10 text-brand-bone/50 border-brand-bone/20",
};

const PAGE_LABELS = {
  home: "Home page",
  licensing: "Licensing page",
  recruitment: "Recruitment page",
  consulting: "Consulting page",
  about: "About page",
  contact: "Contact page",
  footer: "Footer",
};

function ContentEditor() {
  const { overrides, refresh } = useContent();
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const groups = useMemo(() => {
    const g = {};
    Object.keys(DEFAULTS).forEach((k) => {
      const page = k.split(".")[0];
      (g[page] = g[page] || []).push(k);
    });
    return g;
  }, []);

  const valueOf = (k) => (k in edits ? edits[k] : overrides[k] ?? DEFAULTS[k]);
  const dirty = Object.keys(edits).length;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", { items: edits });
      setEdits({});
      await refresh();
      toast.success("Site text updated — live on the website now.");
    } catch {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const reset = async (k) => {
    try {
      await api.delete(`/content/${encodeURIComponent(k)}`);
      setEdits((e) => {
        const c = { ...e };
        delete c[k];
        return c;
      });
      await refresh();
      toast.success("Reset to original text");
    } catch {
      toast.error("Failed to reset");
    }
  };

  return (
    <div data-testid="content-editor">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <p className="text-sm text-brand-bone/60 max-w-2xl font-light leading-relaxed">
          Edit any wording on the site and press save — the page updates instantly for every visitor.
          Wrap words in <span className="text-brand-gold">*asterisks*</span> to make them italic accent text in
          headings. Fields marked <span className="text-brand-gold">items</span> are lists: one entry per line.
          A gold dot marks text you've customised; use reset to bring back the original.
        </p>
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="content-save-button"
          className="rounded-full bg-brand-gold px-7 py-3 text-sm font-semibold text-brand-night transition-all duration-300 hover:bg-brand-goldlight disabled:opacity-40 shrink-0"
        >
          {saving ? "Saving..." : dirty ? `Save ${dirty} change${dirty > 1 ? "s" : ""}` : "No unsaved changes"}
        </button>
      </div>

      {Object.entries(groups).map(([page, keys]) => (
        <div key={page} className="mb-14" data-testid={`content-group-${page}`}>
          <h3 className="font-serif text-2xl text-brand-bone border-b border-brand-bone/10 pb-3 mb-6">
            {PAGE_LABELS[page] || page}
          </h3>
          <div className="space-y-6">
            {keys.map((k) => {
              const v = valueOf(k);
              const isOverridden = overrides[k] != null;
              const isList = k.endsWith(".items");
              return (
                <div key={k} data-testid={`content-field-${k}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs uppercase tracking-[0.15em] text-brand-bone/50">
                      {k.split(".").slice(1).join(" · ")}
                    </span>
                    {isOverridden && (
                      <>
                        <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" title="Customised" />
                        <button
                          onClick={() => reset(k)}
                          data-testid={`content-reset-${k}`}
                          className="text-xs text-brand-gold hover:text-brand-goldlight underline underline-offset-2"
                        >
                          reset
                        </button>
                      </>
                    )}
                  </div>
                  <Textarea
                    value={v}
                    rows={isList ? Math.max(3, String(v).split("\n").length) : String(v).length > 160 ? 5 : 2}
                    onChange={(e) => setEdits((ed) => ({ ...ed, [k]: e.target.value }))}
                    data-testid={`content-input-${k}`}
                    className="bg-brand-deep/40 border-brand-bone/15 text-brand-bone text-sm focus-visible:ring-brand-gold"
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function AccountSettings() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated — use it next time you log in");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const fieldCls = "bg-brand-deep/40 border-brand-bone/15 text-brand-bone h-12 focus-visible:ring-brand-gold";

  return (
    <div data-testid="account-settings" className="max-w-lg">
      <h3 className="font-serif text-2xl text-brand-bone border-b border-brand-bone/10 pb-3 mb-6">Account</h3>
      <p className="text-sm text-brand-bone/60 font-light mb-8">
        Your admin login email is <span className="text-brand-gold">{user?.email}</span>. Change your password
        below — the new one takes effect immediately.
      </p>
      <form onSubmit={submit} className="space-y-5" data-testid="change-password-form">
        <div className="space-y-2">
          <Label htmlFor="current-password" className="text-brand-bone/70">Current password</Label>
          <Input id="current-password" data-testid="current-password-input" type="password" required autoComplete="current-password"
            value={form.current_password} onChange={(e) => setForm((f) => ({ ...f, current_password: e.target.value }))} className={fieldCls} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="new-password" className="text-brand-bone/70">New password (min. 8 characters)</Label>
          <Input id="new-password" data-testid="new-password-input" type="password" required minLength={8} autoComplete="new-password"
            value={form.new_password} onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))} className={fieldCls} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password" className="text-brand-bone/70">Confirm new password</Label>
          <Input id="confirm-password" data-testid="confirm-password-input" type="password" required minLength={8} autoComplete="new-password"
            value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))} className={fieldCls} />
        </div>
        <button type="submit" data-testid="change-password-submit" disabled={saving}
          className="inline-flex items-center gap-3 rounded-full bg-brand-gold px-7 py-3 text-sm font-semibold text-brand-night transition-all duration-300 hover:bg-brand-goldlight disabled:opacity-50">
          <LockKey size={16} weight="duotone" />
          {saving ? "Updating..." : "Update password"}
        </button>
      </form>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/enquiries");
      setEnquiries(data);
    } catch {
      toast.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`);
      setEnquiries((list) => list.filter((e) => e.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Failed to delete enquiry");
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const counts = {
    total: enquiries.length,
    new: enquiries.filter((e) => e.status === "new").length,
    active: enquiries.filter((e) => e.status === "contacted" || e.status === "in-progress").length,
    closed: enquiries.filter((e) => e.status === "closed").length,
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-brand-night text-brand-bone">
      <header className="border-b border-brand-bone/10 bg-brand-deep/40 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/mayfair-logo.webp" alt="" className="h-9 w-auto object-contain rounded-sm" />
            <span className="font-serif text-lg">Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-brand-bone/50" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={load} data-testid="refresh-button" className="p-2 text-brand-bone/60 hover:text-brand-gold transition-colors" aria-label="Refresh">
              <ArrowClockwise size={18} />
            </button>
            <button onClick={onLogout} data-testid="logout-button" className="inline-flex items-center gap-2 rounded-full border border-brand-bone/20 px-4 py-1.5 text-xs hover:border-brand-gold hover:text-brand-gold transition-colors">
              <SignOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        <div className="flex gap-3 mb-10" data-testid="admin-tabs">
          <button
            onClick={() => setTab("enquiries")}
            data-testid="tab-enquiries"
            className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${
              tab === "enquiries" ? "bg-brand-gold text-brand-night" : "border border-brand-bone/20 text-brand-bone/60 hover:text-brand-gold hover:border-brand-gold"
            }`}
          >
            Enquiries
          </button>
          <button
            onClick={() => setTab("content")}
            data-testid="tab-content"
            className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${
              tab === "content" ? "bg-brand-gold text-brand-night" : "border border-brand-bone/20 text-brand-bone/60 hover:text-brand-gold hover:border-brand-gold"
            }`}
          >
            Edit Site Text
          </button>
          <button
            onClick={() => setTab("account")}
            data-testid="tab-account"
            className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${
              tab === "account" ? "bg-brand-gold text-brand-night" : "border border-brand-bone/20 text-brand-bone/60 hover:text-brand-gold hover:border-brand-gold"
            }`}
          >
            Account
          </button>
        </div>

        {tab === "content" ? (
          <ContentEditor />
        ) : tab === "account" ? (
          <AccountSettings />
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-brand-bone/10 border border-brand-bone/10 mb-10" data-testid="stats-row">
              {[
                ["Total", counts.total],
                ["New", counts.new],
                ["Active", counts.active],
                ["Closed", counts.closed],
              ].map(([label, value]) => (
                <div key={label} className="bg-brand-night px-6 py-6" data-testid={`stat-${label.toLowerCase()}`}>
                  <p className="font-serif text-4xl text-brand-gold">{value}</p>
                  <p className="mt-1 text-xs uppercase tracking-[0.2em] text-brand-bone/50">{label}</p>
                </div>
              ))}
            </div>

            <div className="border border-brand-bone/10" data-testid="enquiries-table-wrapper">
              <Table>
                <TableHeader>
                  <TableRow className="border-brand-bone/10 hover:bg-transparent">
                    <TableHead className="text-brand-bone/50">Date</TableHead>
                    <TableHead className="text-brand-bone/50">Name</TableHead>
                    <TableHead className="text-brand-bone/50">Profession</TableHead>
                    <TableHead className="text-brand-bone/50">Country</TableHead>
                    <TableHead className="text-brand-bone/50">Service</TableHead>
                    <TableHead className="text-brand-bone/50">Contact</TableHead>
                    <TableHead className="text-brand-bone/50">Message</TableHead>
                    <TableHead className="text-brand-bone/50">Status</TableHead>
                    <TableHead className="text-brand-bone/50 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow className="border-brand-bone/10 hover:bg-transparent">
                      <TableCell colSpan={9} className="text-center py-16 text-brand-bone/40" data-testid="table-loading">Loading enquiries...</TableCell>
                    </TableRow>
                  ) : enquiries.length === 0 ? (
                    <TableRow className="border-brand-bone/10 hover:bg-transparent">
                      <TableCell colSpan={9} className="text-center py-16 text-brand-bone/40" data-testid="table-empty">No enquiries yet. They will appear here as the form is submitted.</TableCell>
                    </TableRow>
                  ) : (
                    enquiries.map((e) => (
                      <TableRow key={e.id} className="border-brand-bone/10 hover:bg-brand-bone/[0.03]" data-testid={`enquiry-row-${e.id}`}>
                        <TableCell className="text-xs text-brand-bone/60 whitespace-nowrap">
                          {e.created_at ? format(new Date(e.created_at), "dd MMM yyyy") : "-"}
                        </TableCell>
                        <TableCell className="font-medium text-brand-bone whitespace-nowrap">{e.full_name}</TableCell>
                        <TableCell className="text-sm text-brand-bone/70">{e.profession}</TableCell>
                        <TableCell className="text-sm text-brand-bone/70">{e.country}</TableCell>
                        <TableCell className="text-sm text-brand-bone/70 max-w-[180px]">{e.service}</TableCell>
                        <TableCell className="text-xs text-brand-bone/60">
                          <div>{e.email}</div>
                          {e.phone && <div className="mt-0.5">{e.phone}</div>}
                          {e.cv_path && (
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/enquiries/${e.id}/cv`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`cv-link-${e.id}`}
                              className="mt-0.5 inline-block text-brand-gold underline underline-offset-2"
                            >
                              Download CV
                            </a>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-brand-bone/60 max-w-[220px] truncate" title={e.message}>{e.message || "-"}</TableCell>
                        <TableCell>
                          <Select value={e.status} onValueChange={(v) => updateStatus(e.id, v)}>
                            <SelectTrigger data-testid={`status-select-${e.id}`} className="h-8 w-[130px] bg-transparent border-brand-bone/15 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.keys(STATUS_STYLES).map((s) => (
                                <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>
                                  <Badge variant="outline" className={`${STATUS_STYLES[s]} capitalize`}>{s}</Badge>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <button onClick={() => remove(e.id)} data-testid={`delete-enquiry-${e.id}`} className="p-2 text-brand-bone/40 hover:text-red-400 transition-colors" aria-label="Delete enquiry">
                            <Trash size={16} />
                          </button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
